import React, { useEffect, useState } from "react";
import { SwapLeftOutlined } from "@ant-design/icons";
import { IFarmInfo, IUserAddress, IUserInfo } from "@utils/Interfaces";
import { Link } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { Button } from "antd";
import { useAuth } from "contexts/AuthContext";
import { useUser } from "contexts/UserContext";
import Helpers from "@utils/Helpers";
import userApi from "services/UserApi";
import InfoForm from "./InfoForm/InfoForm";
import PasswordForm from "./PasswordForm/PasswordForm";
import "./SignUp.scss";
import FarmForm from "./FarmForm/FarmForm";
import Validates from "@utils/Validates";
import { useGallery } from "contexts/GalleryContext";
import { errorLog } from "logging";
import OtpForm from "@containers/OtpForm/OtpForm";
import { buildSignUpPayload } from "./signUpPayload";

const SignUp: React.FC = () => {
  const { updateUserProfile, fetchAllAddress } = useUser();
  const { fetchtGallery } = useGallery();
  const urlParams = new URLSearchParams(window.location.search);
  const googleId = urlParams.get("googleId");
  const emailVerifying = urlParams.get("targetEmail");
  const { login, setLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isOtpError, setIsOtpError] = useState(false);
  const [formSignUp, setFormSignUp] = useState<IUserInfo & IUserAddress>({
    google_id: "",
    first_name: "",
    last_name: "",
    display_name: "",
    street: "",
    apartment: null,
    city: "",
    province: "",
    postal_code: null,
    email: "",
    phone: null,
    avatar: null,
    password: null,
    lat: 0,
    lon: 0,
    type_ids: [1],
    farm_info: {
      banner: null,
      avatar: "",
      category_ids: [1],
      farm_name: "",
      farm_size: 0,
      street: "",
      postal_code: null,
      city: "",
      province: "",
      website: "",
      farm_bio: "",
      lat: 0,
      lon: 0,
      farm_images: [""],
    },
  });

  useEffect(() => {
    if (googleId !== undefined) {
      setFormSignUp({
        ...formSignUp,
        google_id: googleId,
        first_name: urlParams.get("first_name") || "",
        last_name: urlParams.get("last_name") || "",
        avatar: urlParams.get("avatar") || "",
        email: urlParams.get("email") || "",
        password: "",
      });
    }
  }, [googleId]);

  useEffect(() => {
    if (emailVerifying) {
      if (googleId) {
        setCurrentStep(1);
      } else {
        setCurrentStep(4);
        setLoading(false);
      }
    }
  }, [emailVerifying]);

  const handleSignUp = async () => {
    if (!Validates.validateSignUp(formSignUp.type_ids)) {
      Helpers.notification.warning("Please select user type");
      return;
    }

    setLoading(true);
    try {
      const response = await userApi.createUser(buildSignUpPayload(formSignUp));
      if (response.status === 200) {
        setLoading(false);
        setCurrentStep(4);
      }
    } catch (error) {
      setLoading(false);
      Helpers.notification.error((error as any).response.data.message);
      errorLog("SignUp.tsx - handleSignUp", error);
    }
  };

  const handleVerifyOtp = async (email: string, otp: string) => {
    try {
      setLoading(true);
      const response = await userApi.verifyOtp({ otp_code: otp, email: email });
      if (response.status === 200) {
        if (response.data.message === "Invalid OTP code") {
          Helpers.notification.warning("Invalid OTP Code. Please try again");
          setIsOtpError(true);
        } else {
          const res = response.data.data;
          handleLoginSuccess(res.access_token, res.refresh_token);
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      Helpers.notification.error("Something wrong in server");
      errorLog("SignUp.tsx - handleSignUp", error);
    }
  };

  const handleResendOTP = async (emailToSend: string) => {
    try {
      await userApi.resendOtp({ email: emailToSend });
    } catch (error) {
      Helpers.notification.error(
        "Can't resend OTP. Something wrong in mail server"
      );
      errorLog("OtpForm.tsx - handleResendOTP", error);
    }
  };

  const handleSetFormSignUp = (
    key: keyof IUserInfo,
    value: any,
    subKey?: keyof IFarmInfo
  ) => {
    value = validateFormSignUp(key, value, subKey);
    if (key !== "farm_info" && subKey === undefined) {
      setFormSignUp({ ...formSignUp, [key]: value });
    } else {
      setFormSignUp({
        ...formSignUp,
        farm_info: { ...formSignUp.farm_info, [subKey!]: value },
      });
    }
  };

  const validateFormSignUp = (key: string, value: any, subKey?: string) => {
    if (key === "phone" || subKey === "farm_size") {
      value = Validates.validateNumberForm(value);
    }
    return value;
  };

  const googleLoginHook = useGoogleLogin({
    onSuccess: async (codeResponse: any) => {
      if (codeResponse?.code) {
        googleSignIn(codeResponse.code);
      }
    },
    flow: "auth-code",
    onError: (err: any) => {
      Helpers.notification.error(
        "Can not login via Google. Please log in manually"
      );
    },
  });

  const googleSignIn = async (code: string) => {
    setLoading(true);
    try {
      const response = await userApi.googleSignIn({ code });
      if (response.status === 200) {
        if (response.data.message === "Login successfully") {
          handleLoginSuccess(
            response.data.data.access_token,
            response.data.data.refresh_token
          );
        } else {
          if (response.data.message === "User does not exist") {
            const data = response.data.data;
            Helpers.redirect(
              `/auth/signup?googleId=${data?.google_id || ""}&first_name=${data?.first_name || ""}&last_name=${data?.last_name || ""}&avatar=${data?.avatar || ""}&email=${data?.email || ""}`
            );
          }
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      Helpers.notification.error("Something wrong in server");
      errorLog("SignUp.tsx - googleSignIn", error);
    }
  };

  const handleLoginSuccess = async (
    accessToken: string,
    refreshToken: string
  ) => {
    login();
    updateUserProfile(accessToken, refreshToken);
    fetchtGallery();
    fetchAllAddress();
    setLoading(false);
  };

  return (
    <div className="SignUp">
      <div className="SignUp-wrap">
        <Button
          className="back-btn"
          variant="link"
          color="default"
          icon={<SwapLeftOutlined />}
          onClick={() => {
            Helpers.confirmModal("Are you sure you want to go back?", () =>
              Helpers.redirect("/")
            );
          }}
        >
          Back to Home
        </Button>

        <div className="signup-title">
          {googleId
            ? "Complete Profile"
            : currentStep !== 4
            ? "Signup"
            : "Verify"}
        </div>
        {currentStep === 1 && (
          <InfoForm
            formSignUp={formSignUp}
            setCurrentStep={setCurrentStep}
            handleSetFormSignUp={handleSetFormSignUp}
            googleLoginHook={googleLoginHook}
            googleId={googleId}
            setLoading={setLoading}
            setFormSignUp={setFormSignUp}
          />
        )}
        {currentStep === 2 && (
          <PasswordForm
            formSignUp={formSignUp}
            setCurrentStep={setCurrentStep}
            handleSetFormSignUp={handleSetFormSignUp}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
          />
        )}
        {currentStep === 3 && (
          <FarmForm
            formSignUp={formSignUp}
            setCurrentStep={setCurrentStep}
            handleSetFormSignUp={handleSetFormSignUp}
            signUp={handleSignUp}
            googleId={googleId}
            setFormSignUp={setFormSignUp}
          />
        )}
        {currentStep === 4 && (
          <OtpForm
            submit={handleVerifyOtp}
            email={formSignUp.email}
            isError={isOtpError}
            setIsError={setIsOtpError}
            handleResendOTP={handleResendOTP}
          />
        )}
        {currentStep !== 4 && (
          <div
            className="signin-btn"
            onClick={() => {
              Helpers.confirmModal("Are you sure you want to go back?", () =>
                Helpers.redirect("/auth")
              );
            }}
          >
            Already have an account? <Link to="/auth">Log In</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignUp;
